import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import LiveStream from './LiveStream';
import whoShouldAttendPhoto from '../assets/about/whoShouldAttend.jpg';

const HIGHLIGHT_TOP_CARDS = [
  {
    id: 'decision',
    valueText: '400+',
    labelKey: 'website.about.statDecisionMakers',
  },
  {
    id: 'speakers',
    valueText: '50+',
    labelKey: 'website.about.statGlobalSpeakers',
  },
];

const HIGHLIGHT_BOTTOM_CARD = {
  id: 'meetings',
  headlineKey: 'website.about.statHighValue',
  labelKey: 'website.about.statCuratedMeetings',
};

const whoShouldAttendItems = [
  {
    id: 'multinational_corporations',
    titleKey: 'website.about.whoShouldAttend.multinational_corporations',
    descriptionKey: 'website.about.whoShouldAttendDescription.multinational_corporations',
  },
  {
    id: 'government_decision_makers',
    titleKey: 'website.about.whoShouldAttend.government_decision_makers',
    descriptionKey: 'website.about.whoShouldAttendDescription.government_decision_makers',
  },
  {
    id: 'sovereign_wealth_funds',
    titleKey: 'website.about.whoShouldAttend.sovereign_wealth_funds',
    descriptionKey: 'website.about.whoShouldAttendDescription.sovereign_wealth_funds',
  },
  {
    id: 'private_equity_firms',
    titleKey: 'website.about.whoShouldAttend.private_equity_firms',
    descriptionKey: 'website.about.whoShouldAttendDescription.private_equity_firms',
  },
  {
    id: 'strategic_operators',
    titleKey: 'website.about.whoShouldAttend.strategic_operators',
    descriptionKey: 'website.about.whoShouldAttendDescription.strategic_operators',
  },
  {
    id: 'family_offices',
    titleKey: 'website.about.whoShouldAttend.family_offices',
    descriptionKey: 'website.about.whoShouldAttendDescription.family_offices',
  },
];

const About = () => {
  const { t, isRTL } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  return (
    <section
      id="about"
      className="relative overflow-hidden bg-white"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      {/* Figma: px 140, py 70, gap 60 */}
      <div className="relative z-10 w-full px-6 sm:px-10 lg:px-[140px] py-[50px] lg:py-[70px] flex flex-col items-center gap-[40px] lg:gap-[60px]">
        <motion.div
          className="w-full flex flex-col items-center gap-[40px] lg:gap-[60px]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          {/* Header — Figma: "Welcome" #51516B 36px 700, description #51516B 16px 400, gap 30 */}
          <div className="w-full flex flex-col items-center justify-center gap-[30px] text-center">
            <motion.h2
              variants={itemVariants}
              className="w-full max-w-4xl mx-auto px-2 text-center text-balance"
              style={{ color: '#51516B', fontSize: 36, fontWeight: 700, lineHeight: '46px' }}
            >
              {t('website.about.badge')}
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="w-full max-w-4xl mx-auto px-2 text-center text-balance"
              style={{ color: '#51516B', fontSize: 16, fontWeight: 400, lineHeight: '24px' }}
            >
              {t('website.about.description')}
            </motion.p>
          </div>

          {/* Content — stats + video; on lg same total height as video (420px) */}
          <div className="w-full flex flex-col lg:flex-row lg:items-stretch gap-8 lg:gap-[31px]">
            {/* LEFT: matches video column height at lg */}
            <div className="w-full lg:flex-1 lg:h-[420px] lg:max-h-[420px] flex flex-col gap-[18px] lg:gap-3 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[18px] lg:gap-3 min-w-0 lg:flex-shrink-0 lg:h-[178px] lg:min-h-0">
                {HIGHLIGHT_TOP_CARDS.map((card) => (
                  <motion.div
                    key={card.id}
                    variants={itemVariants}
                    className="p-6 sm:p-[25px] lg:h-full flex flex-col items-stretch gap-4 min-w-0 overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.60) 0%, rgba(255, 255, 255, 0.20) 100%)',
                      boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.05)',
                      borderRadius: 15,
                      outline: '0.5px solid #D6D6D6',
                      outlineOffset: '-0.5px',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <div className="w-full min-w-0 pt-3 sm:pt-[15px] pb-4 border-b border-[#D6D6D6] flex flex-col justify-center items-start min-h-[96px] sm:min-h-[105px]">
                      <div
                        className="w-full text-[clamp(2.25rem,6vw,4rem)] sm:text-[52px] lg:text-[56px] xl:text-[64px] leading-[1.05] tracking-tight break-words hyphens-auto"
                        style={{ color: '#2C3F6E', fontWeight: 700 }}
                      >
                        {card.valueText}
                      </div>
                    </div>
                    <div
                      className="w-full min-w-0 text-start break-words"
                      style={{ color: '#0099CE', fontSize: 18, fontWeight: 500, lineHeight: '21.6px' }}
                    >
                      {t(card.labelKey)}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                key={HIGHLIGHT_BOTTOM_CARD.id}
                variants={itemVariants}
                className="w-full min-w-0 flex-1 min-h-0 p-6 sm:p-8 lg:px-5 lg:py-3 flex flex-col items-stretch gap-5 sm:gap-6 lg:gap-2 overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.60) 0%, rgba(255, 255, 255, 0.20) 100%)',
                  boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.05)',
                  borderRadius: 15,
                  outline: '0.5px solid #D6D6D6',
                  outlineOffset: '-0.5px',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <div
                className="w-full min-w-0 flex-1 flex flex-col justify-center items-start border-b border-[#D6D6D6]"
                style={{ minHeight: '120px' }}
                >
                  <div
                  className="w-full text-[clamp(1.875rem,4vw,2.75rem)] sm:text-[clamp(2rem,4.5vw,3rem)] lg:text-[36px] leading-[1.05] lg:leading-tight tracking-tight break-words hyphens-auto"
                  style={{ color: '#2C3F6E', fontWeight: 700 }}
                  >
                    {t(HIGHLIGHT_BOTTOM_CARD.headlineKey)}
                    </div>
                    </div>
                <div
                  className="w-full min-w-0 text-start break-words text-base sm:text-lg lg:text-[15px] font-medium leading-snug sm:leading-relaxed lg:leading-[1.35] max-w-3xl shrink-0"
                  style={{ color: '#0099CE' }}
                >
                  {t(HIGHLIGHT_BOTTOM_CARD.labelKey)}
                </div>
              </motion.div>
            </div>

            {/* RIGHT: Video/Image — Figma: 565×420, rounded 15, bg #F1F3F7 */}
            <motion.div
              variants={itemVariants}
              className="w-full lg:w-1/2 relative overflow-hidden shrink-0 lg:h-[420px]"
              style={{
                borderRadius: 15,
                background: '#F1F3F7',
                boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div className="relative w-full h-[260px] sm:h-[340px] lg:h-full rounded-[15px] overflow-hidden">
                <div className="absolute inset-0">
                  <LiveStream embedded={true} />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Who Should Attend? — Figma: gradient 163deg, image + stacked list, gap 80 */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full overflow-hidden"
        style={{
          background: 'linear-gradient(163deg, #0AB0CE 0%, #02103C 100%)',
        }}
      >
        <div className="w-full max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-16 py-14 sm:py-20 lg:py-[112px] flex flex-col items-center gap-12 lg:gap-20">
          <h3
            className="w-full max-w-[768px] text-center text-white text-[32px] sm:text-[36px] lg:text-[40px] font-bold leading-[1.2] lg:leading-[48px]"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {t('website.about.whoShouldAttendTitle')}
          </h3>

          <div
            className={`w-full flex flex-col lg:flex-row items-stretch justify-center gap-10 lg:gap-20 min-w-0 ${
              isRTL ? 'lg:flex-row-reverse' : ''
            }`}
          >
            <div className="w-full lg:flex-1 min-w-0 shrink-0">
              <img
                src={whoShouldAttendPhoto}
                alt={t('website.about.whoShouldAttendImageAlt')}
                className="w-full h-[min(70vw,420px)] sm:h-[min(60vw,520px)] lg:h-[640px] rounded-[28px] sm:rounded-[36px] lg:rounded-[42px] object-cover shadow-lg"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="w-full lg:flex-1 min-w-0 flex flex-col justify-center gap-8 py-1">
              {whoShouldAttendItems.map((item) => (
                <div key={item.id} className="flex flex-col items-start text-start gap-4">
                  <div
                    className="text-white text-[22px] sm:text-[24px] font-bold leading-[1.4]"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: '33.6px' }}
                  >
                    {t(item.titleKey)}
                  </div>
                  <div
                    className="text-white text-base font-normal leading-6"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {t(item.descriptionKey)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default About;
